import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ArrowRight, RotateCcw, Home, Dice5, Dumbbell } from 'lucide-react';
import { Mascot } from '../components/game/Mascot';
import { Confetti } from '../components/game/Confetti';
import { speak } from '../lib/speech';
import { playCelebrate } from '../lib/sound';

const ICONS = { next: ArrowRight, replay: RotateCcw, home: Home, dice: Dice5, review: Dumbbell };

export default function ResultScreen({ name, stats, title, subtitle, actions = [], voiceId, volume }) {
  useEffect(() => {
    playCelebrate();
    speak(`${title.replace(/[🎉🏆⭐]/g, '')} ${name}!`, { voiceId, volume });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-lg flex-col items-center justify-center px-5 py-8">
      <Confetti show count={60} />
      <motion.div initial={{ scale: 0.5, y: 30 }} animate={{ scale: 1, y: 0 }} transition={{ type: 'spring' }}>
        <Mascot mood="cheer" size={150} />
      </motion.div>

      <motion.h1
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mt-2 text-center font-fred text-3xl font-bold text-emerald-600 sm:text-4xl"
        data-testid="result-title"
      >
        {title}
      </motion.h1>
      <p className="mb-4 mt-1 text-center text-lg font-bold text-purple-700">{subtitle}</p>

      {/* stars earned */}
      <div className="mb-4 flex gap-2">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.3 + i * 0.2, type: 'spring' }}
          >
            <Star
              className="h-14 w-14"
              style={{
                fill: i < (stats?.stars || 0) ? '#FBBF24' : '#E5E7EB',
                color: i < (stats?.stars || 0) ? '#D97706' : '#D1D5DB',
              }}
            />
          </motion.div>
        ))}
      </div>

      <div className="card-soft mb-6 grid w-full grid-cols-3 gap-2 p-4 text-center">
        <div>
          <p className="font-fred text-3xl font-bold text-emerald-500" data-testid="result-correct">
            {stats?.correct ?? 0}
          </p>
          <p className="text-xs font-black text-slate-500">de {stats?.total ?? 0} certos</p>
        </div>
        <div>
          <p className="font-fred text-3xl font-bold text-purple-500">{stats?.points ?? 0}</p>
          <p className="text-xs font-black text-slate-500">pontos 🏆</p>
        </div>
        <div>
          <p className="font-fred text-3xl font-bold text-orange-500">{stats?.bestStreak ?? 0}</p>
          <p className="text-xs font-black text-slate-500">sequência 🔥</p>
        </div>
      </div>

      <div className="flex w-full flex-col gap-3">
        {actions.map((a, i) => {
          const Icon = ICONS[a.icon] || ArrowRight;
          return (
            <button
              key={a.testid || a.label}
              data-testid={a.testid}
              onClick={a.onClick}
              className="btn-3d w-full px-5 py-4 text-lg"
              style={{ background: a.color, borderColor: a.border, color: a.text || '#fff' }}
            >
              <Icon className="h-5 w-5" /> {a.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
